import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { TransferGroupMemberEndpointType } from '../../types';
import {
  RING_TIMEOUT_MAX_SECONDS,
  RING_TIMEOUT_MIN_SECONDS,
  newEndpointRow,
  type TransferGroupEndpointFormRow,
  type TransferGroupMemberFormRow,
} from './memberForm';

interface AgentSelectedMemberEditorProps {
  member: TransferGroupMemberFormRow;
  fallbackKeys: string[];
  disabled: boolean;
  onChange: (partial: Partial<TransferGroupMemberFormRow>) => void;
  onRemove: () => void;
}

/**
 * Editor for one member of an agent-selected transfer group.
 *
 * External contacts carry their own endpoints, tried in the listed order;
 * user members are reached at their own telephony endpoint.
 */
export default function AgentSelectedMemberEditor({
  member,
  fallbackKeys,
  disabled,
  onChange,
  onRemove,
}: AgentSelectedMemberEditorProps) {
  const { t } = useTranslation(['pages', 'common']);
  const isExternal = member.kind === 'external';

  const updateEndpoint = (index: number, partial: Partial<TransferGroupEndpointFormRow>) => {
    onChange({
      endpoints: member.endpoints.map((endpoint, endpointIndex) => (
        endpointIndex === index ? { ...endpoint, ...partial } : endpoint
      )),
    });
  };

  const moveEndpoint = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= member.endpoints.length) {
      return;
    }
    const endpoints = [...member.endpoints];
    [endpoints[index], endpoints[target]] = [endpoints[target], endpoints[index]];
    onChange({ endpoints });
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color={isExternal ? 'secondary' : 'primary'}
            label={t(isExternal ? 'pages:transferGroups.member.kindExternal' : 'pages:transferGroups.member.kindUser')}
          />
          {!isExternal && <Chip size="small" variant="outlined" label={member.userLabel || member.userId} />}
          <Box sx={{ flexGrow: 1 }} />
          <FormControlLabel
            control={(
              <Switch
                checked={member.isActive}
                onChange={(event) => onChange({ isActive: event.target.checked })}
                disabled={disabled}
              />
            )}
            label={t('common:status.active')}
          />
          <IconButton color="error" disabled={disabled} onClick={onRemove} aria-label={t('common:actions.delete')}>
            <Delete />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 2fr 1fr' } }}>
          <TextField
            label={t('pages:transferGroups.member.selectionKey')}
            helperText={t('pages:transferGroups.member.selectionKeyHelper')}
            value={member.selectionKey}
            onChange={(event) => onChange({ selectionKey: event.target.value })}
            required
            disabled={disabled}
            inputProps={{ maxLength: 32 }}
          />
          <TextField
            label={t('pages:transferGroups.member.displayName')}
            helperText={isExternal ? undefined : t('pages:transferGroups.member.displayNameUserHelper')}
            value={member.displayName}
            onChange={(event) => onChange({ displayName: event.target.value })}
            required={isExternal}
            disabled={disabled}
            inputProps={{ maxLength: 120 }}
          />
          <FormControl disabled={disabled}>
            <InputLabel>{t('pages:transferGroups.member.fallback')}</InputLabel>
            <Select
              value={fallbackKeys.includes(member.fallbackSelectionKey) ? member.fallbackSelectionKey : ''}
              label={t('pages:transferGroups.member.fallback')}
              onChange={(event) => onChange({ fallbackSelectionKey: String(event.target.value) })}
            >
              <MenuItem value="">{t('pages:transferGroups.member.noFallback')}</MenuItem>
              {fallbackKeys.map((key) => (
                <MenuItem key={key} value={key}>{key}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={member.aliases}
          disabled={disabled}
          onChange={(_, aliases) => onChange({ aliases: aliases.map((alias) => alias.trim()).filter(Boolean) })}
          renderTags={(value, getTagProps) => value.map((alias, index) => (
            <Chip {...getTagProps({ index })} key={alias} label={alias} size="small" />
          ))}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('pages:transferGroups.member.aliases')}
              helperText={t('pages:transferGroups.member.aliasesHelper')}
            />
          )}
        />

        <TextField
          label={t('pages:transferGroups.member.description')}
          helperText={t('pages:transferGroups.member.descriptionHelper')}
          value={member.description}
          onChange={(event) => onChange({ description: event.target.value })}
          disabled={disabled}
          inputProps={{ maxLength: 280 }}
          multiline
          minRows={1}
        />

        {isExternal ? (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="subtitle2">{t('pages:transferGroups.member.endpoints')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pages:transferGroups.member.endpointsHelper')}
            </Typography>
            {member.endpoints.map((endpoint, index) => (
              <Box
                key={endpoint.rowKey}
                sx={{
                  display: 'grid',
                  gap: 1,
                  alignItems: 'center',
                  gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.5fr 1fr auto auto' },
                }}
              >
                <FormControl size="small" disabled={disabled}>
                  <InputLabel>{t('pages:transferGroups.member.endpointType')}</InputLabel>
                  <Select
                    value={endpoint.endpointType}
                    label={t('pages:transferGroups.member.endpointType')}
                    onChange={(event) => updateEndpoint(index, {
                      endpointType: event.target.value as TransferGroupMemberEndpointType,
                    })}
                  >
                    <MenuItem value="external_extension">{t('pages:transferGroups.member.endpointTypes.external_extension')}</MenuItem>
                    <MenuItem value="external_phone">{t('pages:transferGroups.member.endpointTypes.external_phone')}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label={t(
                    endpoint.endpointType === 'external_phone'
                      ? 'pages:transferGroups.member.phone'
                      : 'pages:transferGroups.member.extension',
                  )}
                  placeholder={endpoint.endpointType === 'external_phone' ? '+5511999999999' : '115'}
                  value={endpoint.value}
                  onChange={(event) => updateEndpoint(index, { value: event.target.value })}
                  disabled={disabled}
                  required
                />
                <TextField
                  size="small"
                  type="number"
                  label={t('pages:transferGroups.member.ringTimeout')}
                  value={endpoint.ringTimeoutSeconds}
                  onChange={(event) => updateEndpoint(index, { ringTimeoutSeconds: Number(event.target.value) })}
                  disabled={disabled}
                  inputProps={{ min: RING_TIMEOUT_MIN_SECONDS, max: RING_TIMEOUT_MAX_SECONDS }}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={endpoint.isActive}
                      onChange={(event) => updateEndpoint(index, { isActive: event.target.checked })}
                      disabled={disabled}
                    />
                  )}
                  label={t('common:status.active')}
                />
                <Box sx={{ display: 'flex' }}>
                  <Tooltip title={t('pages:transferGroups.member.moveUp')}>
                    <span>
                      <IconButton size="small" disabled={disabled || index === 0} onClick={() => moveEndpoint(index, -1)}>
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('pages:transferGroups.member.moveDown')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={disabled || index === member.endpoints.length - 1}
                        onClick={() => moveEndpoint(index, 1)}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={() => onChange({ endpoints: member.endpoints.filter((_, endpointIndex) => endpointIndex !== index) })}
                    aria-label={t('common:actions.delete')}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Box>
              <Button
                size="small"
                startIcon={<Add />}
                disabled={disabled}
                onClick={() => onChange({
                  endpoints: [...member.endpoints, newEndpointRow(member.endpoints.length ? 'external_phone' : 'external_extension')],
                })}
              >
                {t('pages:transferGroups.member.addEndpoint')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('pages:transferGroups.member.userEndpointHelper')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
